import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import {
  getMetricsCache,
  setMetricsCache,
  getPendingFetch,
  getDailyRowsCache,
  getSetterNamesCache,
} from "../lib/setterPerformanceCacheService";
import { aggregateDailyRows, type DailyRow } from "../lib/aggregateDailyRows";
import { getDateKeyInTZ, toOrgStartISO, toOrgEndISO, toOrgDateKey } from "../lib/timezone";

// =============================================================================
// USE SETTER PERFORMANCE HOOK
// =============================================================================
// PURPOSE: Fetch setter performance metrics for the
// Setter Performance tab on the DM Analytics page.
//
// DATA SOURCES:
// - get_setter_performance RPC: Speed to lead, response time, session metrics
//
// FEATURES:
// - AbortController cleanup on unmount/dependency change
// - Loading/error states for UI feedback
// - Force refresh capability
// =============================================================================

// Types for setter performance metrics
export interface SetterMetrics {
  setter_id: string;
  setter_name: string;
  total_conversations: number;
  real_conversations: number;
  total_messages_sent: number;
  // Date-scoped sum of words across all setter-sent msgs (drives Messaging card avg w/msg)
  total_words_sent: number;
  // Engaged response time (reply speed while actively on phone)
  engaged_response_avg_mins: number | null;
  engaged_response_median_mins: number | null;
  engaged_response_count: number;
  // Session gap (time between work sessions)
  session_gap_avg_mins: number | null;
  session_gap_median_mins: number | null;
  session_count: number;
  total_work_minutes: number;
  // Lead reply time (how fast leads respond to setters)
  lead_reply_avg_mins: number | null;
  lead_reply_median_mins: number | null;
  lead_reply_count: number;
  // Average reply time (overall response speed, not filtered by session)
  avg_reply_time_avg_mins: number | null;
  avg_reply_time_median_mins: number | null;
  avg_reply_time_count: number;
  // Ignored conversations (leads unreplied while setter was active)
  total_ignored_conversations: number;
  total_ignore_events: number;
  avg_ignored_per_session: number | null;
  // Work pattern (from daily_activity aggregation)
  work_days_count: number;
  avg_first_msg_minute_of_day: number | null; // minutes since midnight (e.g., 705 = 11:45 AM)
  avg_last_msg_minute_of_day: number | null; // minutes since midnight (e.g., 1278 = 9:18 PM)
  avg_active_window_mins: number | null;
  avg_work_mins_per_day: number | null;
  avg_break_mins_per_day: number | null;
  longest_gap_mins: number | null;
  // Reply turn distribution (granular depth: 0-10 individual, 11-12, 13-18, 19+)
  avg_reply_turns: number | null;
  total_reply_turns: number;
  reply_turn_0: number;
  reply_turn_1: number;
  reply_turn_2: number;
  reply_turn_3: number;
  reply_turn_4: number;
  reply_turn_5: number;
  reply_turn_6: number;
  reply_turn_7: number;
  reply_turn_8: number;
  reply_turn_9: number;
  reply_turn_10: number;
  reply_turn_11_12: number;
  reply_turn_13_18: number;
  reply_turn_19plus: number;
  // Lead-last counts (lead sent last message → setter owes a reply)
  reply_turn_0_lead: number;
  reply_turn_1_lead: number;
  reply_turn_2_lead: number;
  reply_turn_3_lead: number;
  reply_turn_4_lead: number;
  reply_turn_5_lead: number;
  reply_turn_6_lead: number;
  reply_turn_7_lead: number;
  reply_turn_8_lead: number;
  reply_turn_9_lead: number;
  reply_turn_10_lead: number;
  reply_turn_11_12_lead: number;
  reply_turn_13_18_lead: number;
  reply_turn_19plus_lead: number;
  // Calls pitched (Calendly links sent to unique conversations)
  calls_pitched: number;
  // Pre-calls filled (DM-attributed sales_pre_call transactions)
  precalls_filled: number;
  // Pre-calls total (all channels, no attribution filter)
  precalls_total: number;
  // Follow-up persistence (how many days setter kept following up per conversation)
  followup_persistence_1d: number;
  followup_persistence_2d: number;
  followup_persistence_3d: number;
  followup_persistence_4d: number;
  followup_persistence_5d: number;
  followup_persistence_6d: number;
  followup_persistence_7d: number;
  followup_persistence_7plus: number;
  // Conversation span (calendar days from first to last message in range)
  convo_span_same_day: number;
  convo_span_1_2d: number;
  convo_span_3_6d: number;
  convo_span_1_2w: number;
  convo_span_2w_plus: number;
  avg_convo_span_days: number | null;
  // S100 setter-performance tiles — 8 now()-anchored counts from
  // get_setter_cross_day_metrics (Live Leak / Depth-Conversion / Deep Cliff-Fall /
  // Actionable-Deep Owed). Range-INDEPENDENT: they scan ghl_conversations live off
  // now(), not the selected date range. Source: sql/setter-performance/005_cross_day_metrics.sql.
  leak_engaged_7d: number;
  leak_untouched_7d: number;
  depth_engaged_30d: number;
  depth_deep_30d: number;
  cliff_all_24_48h: number;
  cliff_deep_24_48h: number;
  actionable_all_lt24h: number;
  actionable_deep_lt24h: number;
  // Orphan pre-calls (pre-calls with no matching call outcome in range)
  // Fetched via get_setter_orphan_pre_calls RPC — NOT in setter_daily_performance.
  // Default 0; merged after daily-row aggregation on both cold and warm cache paths.
  orphan_pre_calls_count: number;
  orphan_pre_calls_error: boolean;
  // Lost pre-calls (pre-calls where the lead was marked lost, no call taken)
  // Fetched via get_setter_lost_pre_calls RPC — NOT in setter_daily_performance.
  // Default 0; merged sequentially after orphan fetch block.
  lost_pre_calls_count: number;
  lost_pre_calls_error: boolean;
  // Cross-day hydration flag: true once get_setter_cross_day_metrics has resolved
  // (success, empty, error, or retry exhaustion — D12 terminal hydration rule).
  // false = resolution pending (show skeleton). true = resolution done (show bars or hide).
  // NON-optional by design — typecheck is the completeness gate for new row-producing paths.
  cross_day_hydrated: boolean;
}

// Hook return type
export interface UseSetterPerformanceResult {
  // Setter metrics (one per setter)
  metrics: SetterMetrics[];
  // States
  isLoading: boolean;
  error: string | null;
  // Actions
  refresh: () => void;
}

/**
 * Parse raw Supabase RPC output into typed SetterMetrics.
 * Supabase returns BIGINT/NUMERIC as strings — this converts them to numbers.
 * Shared between useSetterPerformance hook and useGlobalPreload.
 */
export function parseSetterMetrics(data: Record<string, unknown>[]): SetterMetrics[] {
  const parseNum = (v: unknown): number | null => (v != null ? parseFloat(v as string) : null);

  return (data || []).map((row) => ({
    ...row,
    total_conversations: Number(row.total_conversations ?? 0),
    real_conversations: Number(row.real_conversations ?? 0),
    total_messages_sent: Number(row.total_messages_sent ?? 0),
    total_words_sent: Number(row.total_words_sent ?? 0),
    engaged_response_avg_mins: parseNum(row.engaged_response_avg_mins),
    engaged_response_median_mins: parseNum(row.engaged_response_median_mins),
    engaged_response_count: Number(row.engaged_response_count ?? 0),
    session_gap_avg_mins: parseNum(row.session_gap_avg_mins),
    session_gap_median_mins: parseNum(row.session_gap_median_mins),
    session_count: Number(row.session_count ?? 0),
    total_work_minutes: parseFloat((row.total_work_minutes as string) ?? "0"),
    lead_reply_avg_mins: parseNum(row.lead_reply_avg_mins),
    lead_reply_median_mins: parseNum(row.lead_reply_median_mins),
    lead_reply_count: Number(row.lead_reply_count ?? 0),
    avg_reply_time_avg_mins: parseNum(row.avg_reply_time_avg_mins),
    avg_reply_time_median_mins: parseNum(row.avg_reply_time_median_mins),
    avg_reply_time_count: Number(row.avg_reply_time_count ?? 0),
    total_ignored_conversations: Number(row.total_ignored_conversations ?? 0),
    total_ignore_events: Number(row.total_ignore_events ?? 0),
    avg_ignored_per_session: parseNum(row.avg_ignored_per_session),
    work_days_count: Number(row.work_days_count ?? 0),
    avg_first_msg_minute_of_day: parseNum(row.avg_first_msg_minute_of_day),
    avg_last_msg_minute_of_day: parseNum(row.avg_last_msg_minute_of_day),
    avg_active_window_mins: parseNum(row.avg_active_window_mins),
    avg_work_mins_per_day: parseNum(row.avg_work_mins_per_day),
    avg_break_mins_per_day: parseNum(row.avg_break_mins_per_day),
    longest_gap_mins: parseNum(row.longest_gap_mins),
    avg_reply_turns: parseNum(row.avg_reply_turns),
    total_reply_turns: Number(row.total_reply_turns ?? 0),
    // Zeroed here — overridden by live get_reply_turn_distribution RPC merge below
    reply_turn_0: 0,
    reply_turn_1: 0,
    reply_turn_2: 0,
    reply_turn_3: 0,
    reply_turn_4: 0,
    reply_turn_5: 0,
    reply_turn_6: 0,
    reply_turn_7: 0,
    reply_turn_8: 0,
    reply_turn_9: 0,
    reply_turn_10: 0,
    reply_turn_11_12: 0,
    reply_turn_13_18: 0,
    reply_turn_19plus: 0,
    reply_turn_0_lead: 0,
    reply_turn_1_lead: 0,
    reply_turn_2_lead: 0,
    reply_turn_3_lead: 0,
    reply_turn_4_lead: 0,
    reply_turn_5_lead: 0,
    reply_turn_6_lead: 0,
    reply_turn_7_lead: 0,
    reply_turn_8_lead: 0,
    reply_turn_9_lead: 0,
    reply_turn_10_lead: 0,
    reply_turn_11_12_lead: 0,
    reply_turn_13_18_lead: 0,
    reply_turn_19plus_lead: 0,
    calls_pitched: Number(row.calls_pitched ?? 0),
    precalls_filled: Number(row.precalls_filled ?? 0),
    precalls_total: Number(row.precalls_total ?? 0),
    followup_persistence_1d: Number(row.followup_persistence_1d ?? 0),
    followup_persistence_2d: Number(row.followup_persistence_2d ?? 0),
    followup_persistence_3d: Number(row.followup_persistence_3d ?? 0),
    followup_persistence_4d: Number(row.followup_persistence_4d ?? 0),
    followup_persistence_5d: Number(row.followup_persistence_5d ?? 0),
    followup_persistence_6d: Number(row.followup_persistence_6d ?? 0),
    followup_persistence_7d: Number(row.followup_persistence_7d ?? 0),
    followup_persistence_7plus: Number(row.followup_persistence_7plus ?? 0),
    convo_span_same_day: Number(row.convo_span_same_day ?? 0),
    convo_span_1_2d: Number(row.convo_span_1_2d ?? 0),
    convo_span_3_6d: Number(row.convo_span_3_6d ?? 0),
    convo_span_1_2w: Number(row.convo_span_1_2w ?? 0),
    convo_span_2w_plus: Number(row.convo_span_2w_plus ?? 0),
    avg_convo_span_days: parseNum(row.avg_convo_span_days),
    // S100 setter-performance tiles — sourced from get_setter_cross_day_metrics,
    // so a daily row never carries them: default 0 here, overlaid by applyCrossDay.
    leak_engaged_7d: Number(row.leak_engaged_7d ?? 0),
    leak_untouched_7d: Number(row.leak_untouched_7d ?? 0),
    depth_engaged_30d: Number(row.depth_engaged_30d ?? 0),
    depth_deep_30d: Number(row.depth_deep_30d ?? 0),
    cliff_all_24_48h: Number(row.cliff_all_24_48h ?? 0),
    cliff_deep_24_48h: Number(row.cliff_deep_24_48h ?? 0),
    actionable_all_lt24h: Number(row.actionable_all_lt24h ?? 0),
    actionable_deep_lt24h: Number(row.actionable_deep_lt24h ?? 0),
    // Orphan/lost counts not in RPC — default 0/false, merged separately after this call
    orphan_pre_calls_count: 0,
    orphan_pre_calls_error: false,
    lost_pre_calls_count: 0,
    lost_pre_calls_error: false,
    // cross_day pending — set true by applyCrossDay when get_setter_cross_day_metrics resolves
    cross_day_hydrated: false,
  })) as SetterMetrics[];
}

// Hook options
export interface UseSetterPerformanceOptions {
  organizationId: string | null;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone?: string; // org timezone for client-side date conversion (default "UTC")
}

/**
 * Applies the live reply-turn DISTRIBUTION (buckets + lead variants + avg)
 * onto setter rows. Returns a NEW array (React-state safe — never mutates).
 *
 * Deliberately does NOT touch total_reply_turns: that stays date-scoped from
 * the daily rows. The live RPC's total_reply_turns is a lifetime SUM and
 * overriding it collapses the SetterScorecard msgs/turn ratio below 1
 * (Apr 26 bug). avg_reply_turns IS lifetime-scoped on purpose — it sits next
 * to the lifetime-bucketed histogram and both must share scope.
 */
export function applyReplyTurnDistribution(
  rows: SetterMetrics[],
  rtData: Array<Record<string, unknown>> | null | undefined
): SetterMetrics[] {
  if (!rtData || rtData.length === 0) return rows;
  const rtMap = new Map<string, Record<string, unknown>>();
  for (const row of rtData) rtMap.set(row.setter_id as string, row);
  return rows.map((m) => {
    const rt = rtMap.get(m.setter_id);
    if (!rt) return m;
    return {
      ...m,
      reply_turn_0: Number(rt.reply_turn_0 ?? 0),
      reply_turn_1: Number(rt.reply_turn_1 ?? 0),
      reply_turn_2: Number(rt.reply_turn_2 ?? 0),
      reply_turn_3: Number(rt.reply_turn_3 ?? 0),
      reply_turn_4: Number(rt.reply_turn_4 ?? 0),
      reply_turn_5: Number(rt.reply_turn_5 ?? 0),
      reply_turn_6: Number(rt.reply_turn_6 ?? 0),
      reply_turn_7: Number(rt.reply_turn_7 ?? 0),
      reply_turn_8: Number(rt.reply_turn_8 ?? 0),
      reply_turn_9: Number(rt.reply_turn_9 ?? 0),
      reply_turn_10: Number(rt.reply_turn_10 ?? 0),
      reply_turn_11_12: Number(rt.reply_turn_11_12 ?? 0),
      reply_turn_13_18: Number(rt.reply_turn_13_18 ?? 0),
      reply_turn_19plus: Number(rt.reply_turn_19plus ?? 0),
      reply_turn_0_lead: Number(rt.reply_turn_0_lead ?? 0),
      reply_turn_1_lead: Number(rt.reply_turn_1_lead ?? 0),
      reply_turn_2_lead: Number(rt.reply_turn_2_lead ?? 0),
      reply_turn_3_lead: Number(rt.reply_turn_3_lead ?? 0),
      reply_turn_4_lead: Number(rt.reply_turn_4_lead ?? 0),
      reply_turn_5_lead: Number(rt.reply_turn_5_lead ?? 0),
      reply_turn_6_lead: Number(rt.reply_turn_6_lead ?? 0),
      reply_turn_7_lead: Number(rt.reply_turn_7_lead ?? 0),
      reply_turn_8_lead: Number(rt.reply_turn_8_lead ?? 0),
      reply_turn_9_lead: Number(rt.reply_turn_9_lead ?? 0),
      reply_turn_10_lead: Number(rt.reply_turn_10_lead ?? 0),
      reply_turn_11_12_lead: Number(rt.reply_turn_11_12_lead ?? 0),
      reply_turn_13_18_lead: Number(rt.reply_turn_13_18_lead ?? 0),
      reply_turn_19plus_lead: Number(rt.reply_turn_19plus_lead ?? 0),
      avg_reply_turns: rt.avg_reply_turns != null ? parseFloat(rt.avg_reply_turns as string) : null,
      // total_reply_turns intentionally NOT set — see JSDoc above.
    };
  });
}

/**
 * True when the orphan/lost chip counts on these rows are already populated
 * (or errored). All-zero, no-error rows are indistinguishable from "never
 * fetched" — callers refetch then (worst case: two cheap RPCs confirm zeros).
 * Shared by mergeOrphanLost and the prefetch warm's skip guard.
 */
export function hasChipCounts(rows: SetterMetrics[]): boolean {
  return rows.some(
    (r) =>
      r.orphan_pre_calls_count > 0 ||
      r.lost_pre_calls_count > 0 ||
      r.orphan_pre_calls_error ||
      r.lost_pre_calls_error
  );
}

/**
 * Applies orphan/lost pre-call counts onto setter rows. Returns a NEW array.
 * Error semantics: on RPC error keep the prior count and set the error flag —
 * identical to the original inline merge in mergeOrphanLost.
 */
export function applyOrphanLost(
  rows: SetterMetrics[],
  orphanData: Array<Record<string, unknown>> | null,
  orphanError: boolean,
  lostData: Array<Record<string, unknown>> | null,
  lostError: boolean
): SetterMetrics[] {
  const orphanMap: Record<string, number> = {};
  for (const row of orphanData || []) {
    orphanMap[row.setter_ghl_user_id as string] = Number(row.orphan_count ?? 0);
  }
  const lostMap: Record<string, number> = {};
  for (const row of lostData || []) {
    lostMap[row.setter_ghl_user_id as string] = Number(row.lost_count ?? 0);
  }
  return rows.map((m) => ({
    ...m,
    orphan_pre_calls_count: orphanError ? m.orphan_pre_calls_count : (orphanMap[m.setter_id] ?? 0),
    orphan_pre_calls_error: orphanError,
    lost_pre_calls_count: lostError ? m.lost_pre_calls_count : (lostMap[m.setter_id] ?? 0),
    lost_pre_calls_error: lostError,
  }));
}

/**
 * Applies cross-day metrics (follow-up persistence + conversation span buckets)
 * onto setter rows. Returns a NEW array (React-state safe — never mutates).
 *
 * D12 TERMINAL HYDRATION RULE: sets cross_day_hydrated=true on EVERY row
 * regardless of whether cdData contained a matching entry for that setter.
 * A returned RPC with no row for a setter = zero activity = terminal.
 * Callers MUST pass [] (not null) on error/empty to trigger flag set.
 * Used by: standalone merge handler, mergeCrossDay, prefetch (A2),
 * and the client-side-aggregation partial-merge site.
 * NOTE: applyCrossDay handles ONLY the 14 cross-day fields. The 4 orphan/lost
 * fields (orphan_pre_calls_count/error, lost_pre_calls_count/error) are NOT
 * touched here — they live in the surrounding spread at the partial-merge site.
 */
export function applyCrossDay(
  rows: SetterMetrics[],
  cdData: Array<Record<string, unknown>> | null
): SetterMetrics[] {
  const cdMap = new Map<string, Record<string, unknown>>();
  for (const row of cdData || []) cdMap.set(row.setter_id as string, row);
  return rows.map((m) => {
    const cd = cdMap.get(m.setter_id);
    return {
      ...m,
      // 14 cross-day fields — avg_convo_span_days uses parseFloat (canonical form)
      followup_persistence_1d: cd
        ? Number(cd.followup_persistence_1d ?? 0)
        : m.followup_persistence_1d,
      followup_persistence_2d: cd
        ? Number(cd.followup_persistence_2d ?? 0)
        : m.followup_persistence_2d,
      followup_persistence_3d: cd
        ? Number(cd.followup_persistence_3d ?? 0)
        : m.followup_persistence_3d,
      followup_persistence_4d: cd
        ? Number(cd.followup_persistence_4d ?? 0)
        : m.followup_persistence_4d,
      followup_persistence_5d: cd
        ? Number(cd.followup_persistence_5d ?? 0)
        : m.followup_persistence_5d,
      followup_persistence_6d: cd
        ? Number(cd.followup_persistence_6d ?? 0)
        : m.followup_persistence_6d,
      followup_persistence_7d: cd
        ? Number(cd.followup_persistence_7d ?? 0)
        : m.followup_persistence_7d,
      followup_persistence_7plus: cd
        ? Number(cd.followup_persistence_7plus ?? 0)
        : m.followup_persistence_7plus,
      convo_span_same_day: cd ? Number(cd.convo_span_same_day ?? 0) : m.convo_span_same_day,
      convo_span_1_2d: cd ? Number(cd.convo_span_1_2d ?? 0) : m.convo_span_1_2d,
      convo_span_3_6d: cd ? Number(cd.convo_span_3_6d ?? 0) : m.convo_span_3_6d,
      convo_span_1_2w: cd ? Number(cd.convo_span_1_2w ?? 0) : m.convo_span_1_2w,
      convo_span_2w_plus: cd ? Number(cd.convo_span_2w_plus ?? 0) : m.convo_span_2w_plus,
      avg_convo_span_days: cd
        ? cd.avg_convo_span_days != null
          ? parseFloat(cd.avg_convo_span_days as string)
          : null
        : m.avg_convo_span_days,
      // S100 setter-performance tiles — PostgREST serialises BIGINT as a STRING
      // (debug #87), so Number() is load-bearing, not cosmetic.
      leak_engaged_7d: cd ? Number(cd.leak_engaged_7d ?? 0) : m.leak_engaged_7d,
      leak_untouched_7d: cd ? Number(cd.leak_untouched_7d ?? 0) : m.leak_untouched_7d,
      depth_engaged_30d: cd ? Number(cd.depth_engaged_30d ?? 0) : m.depth_engaged_30d,
      depth_deep_30d: cd ? Number(cd.depth_deep_30d ?? 0) : m.depth_deep_30d,
      cliff_all_24_48h: cd ? Number(cd.cliff_all_24_48h ?? 0) : m.cliff_all_24_48h,
      cliff_deep_24_48h: cd ? Number(cd.cliff_deep_24_48h ?? 0) : m.cliff_deep_24_48h,
      actionable_all_lt24h: cd ? Number(cd.actionable_all_lt24h ?? 0) : m.actionable_all_lt24h,
      actionable_deep_lt24h: cd ? Number(cd.actionable_deep_lt24h ?? 0) : m.actionable_deep_lt24h,
      // D12: flag set true on EVERY row — resolution is terminal whether matched or not
      cross_day_hydrated: true,
    };
  });
}

export function useSetterPerformance(
  options: UseSetterPerformanceOptions
): UseSetterPerformanceResult {
  const { organizationId, setterIds, dateRange, timezone = "UTC" } = options;

  // State — initialize from cache synchronously to avoid empty-state render
  const [metrics, setMetrics] = useState<SetterMetrics[]>(
    () =>
      (organizationId && dateRange.start && dateRange.end
        ? getMetricsCache<SetterMetrics[]>(organizationId, dateRange)
        : null) ?? []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Abort controller ref
  const abortRef = useRef<AbortController | null>(null);
  // Track last successful fetch to skip redundant background re-fetches on navigate-back
  const lastFetchTimeRef = useRef<number>(0);
  const RECENCY_GUARD_MS = 60_000; // Skip background re-fetch if cache < 60s old
  // Generation counter for the main metrics fetch — prevents a stale async callback
  // (e.g. mergeReplyTurns IIFE, replyTurnPromise.then) from writing to state after a
  // newer fetch has already started. The functional-update form of setMetrics does NOT
  // prevent stale closures: a queued `setMetrics((prev) => applyRT(prev, staleRtData))`
  // is applied against the CURRENT prev at render time, corrupting the newer range's data.
  const metricsGenRef = useRef(0);

  // Fetch setter metrics (stale-while-revalidate)
  // Main metrics + cross-day metrics fetched in parallel for performance.
  // ensure_fresh is fire-and-forget (non-blocking) — stale-while-revalidate handles the gap.
  //
  // COORDINATION: Before starting our own fetch, check if the prefetch (useDMAnalyticsPrefetch)
  // already has this range in-flight. If so, await its Promise instead of duplicating the RPCs.
  const fetchMetrics = useCallback(async () => {
    if (!organizationId || !supabase || setterIds.length === 0) return;
    if (!dateRange.start || !dateRange.end) return;

    // Abort previous request
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const myController = abortRef.current;

    // Stamp this fetch's generation. Any setMetrics functional update must check
    // metricsGenRef.current === myGen before writing, so a stale async closure
    // (e.g. mergeReplyTurns IIFE that resolved before abort) cannot overwrite a
    // newer fetch's state. Without this, a queued `(prev) => applyRT(prev, staleRt)`
    // fires against the current prev at render time, producing mixed-range data.
    metricsGenRef.current++;
    const myGen = metricsGenRef.current;

    // Track whether client-side aggregation populated data for THIS fetch.
    // Used at the end to decide: partial merge (cross-day only) vs full replace.
    let clientSideAggregationUsed = false;

    // Orphan/lost pre-call counts ride ONLY the full in-hook fetch below — but the
    // prefetch-consume and recency-guard paths return before it runs, and both the
    // prefetch (useDMAnalyticsPrefetch) and aggregateDailyRows() hardcode the counts
    // to 0. Without this decoupled merge, the orphan/lost chips silently vanish on
    // every prefetch-warmed range (today / this month / last month) after a fresh
    // page load. Fire-and-forget, mirrors the replyTurnPromise pattern.
    const mergeOrphanLost = (rows: SetterMetrics[]) => {
      if (rows.length === 0) return;
      // A prior full fetch (or prefetch warm) already merged real counts → skip.
      if (hasChipCounts(rows)) return;
      const chipParams = {
        p_organization_id: organizationId,
        p_setter_ghl_user_ids: rows.map((r) => r.setter_id),
        p_start_date: toOrgDateKey(dateRange.start!),
        p_end_date: toOrgDateKey(dateRange.end!),
      };
      void (async () => {
        const [orphanRes, lostRes] = await Promise.all([
          supabase!.rpc("get_setter_orphan_pre_calls", chipParams).abortSignal(myController.signal),
          supabase!.rpc("get_setter_lost_pre_calls", chipParams).abortSignal(myController.signal),
        ]);
        if (myController.signal.aborted) return; // superseded by a newer fetch
        if (metricsGenRef.current !== myGen) return; // stale generation — discard
        const apply = (ms: SetterMetrics[]) =>
          applyOrphanLost(ms, orphanRes.data, !!orphanRes.error, lostRes.data, !!lostRes.error);
        setMetrics((prev) => apply(prev));
        // Keep the cache consistent so the next load renders chips instantly.
        const cachedNow = getMetricsCache<SetterMetrics[]>(organizationId, dateRange);
        if (cachedNow) setMetricsCache(organizationId, dateRange, apply(cachedNow));
      })();
    };

    // Shared retry helper for reply-turn RPC. Fires the RPC immediately; on error
    // waits ~2500ms and fires once more. Returns data array or null.
    // The first attempt already warms Postgres buffers so the retry lands fast.
    const fetchReplyTurnsWithRetry = async (
      signal: AbortSignal
    ): Promise<Array<Record<string, unknown>> | null> => {
      const rtParams = {
        p_organization_id: organizationId,
        p_setter_ids: setterIds,
        p_start_date: toOrgStartISO(dateRange.start!, timezone),
        p_end_date: toOrgEndISO(dateRange.end!, timezone),
      };
      const attempt = await Promise.resolve(
        supabase!.rpc("get_reply_turn_distribution", rtParams).abortSignal(signal)
      )
        .then((r) => ({ data: r.data, error: r.error }))
        .catch((e: unknown) => ({ data: null, error: e }));
      if (!attempt.error && attempt.data) {
        return attempt.data as Array<Record<string, unknown>>;
      }
      if (signal.aborted) return null; // aborted ≠ failed — no warn, no retry
      // First attempt failed — wait 2500ms and retry once
      console.warn("Reply turn distribution fetch failed (will retry):", attempt.error);
      await new Promise((r) => setTimeout(r, 2500));
      if (signal.aborted) return null;
      const retry = await Promise.resolve(
        supabase!.rpc("get_reply_turn_distribution", rtParams).abortSignal(signal)
      )
        .then((r) => ({ data: r.data, error: r.error }))
        .catch((e: unknown) => ({ data: null, error: e }));
      if (!retry.error && retry.data) {
        return retry.data as Array<Record<string, unknown>>;
      }
      console.warn("Reply turn distribution retry also failed:", retry.error);
      return null;
    };

    // Decoupled reply-turn fetch for prefetch/cache early-return paths.
    // Fires only when the reply-turn buckets are all-zero (never populated).
    // Uses fetchReplyTurnsWithRetry so it gets the same retry logic as the
    // full-fetch path. Fire-and-forget — never blocks render.
    const mergeReplyTurns = (rows: SetterMetrics[]) => {
      if (rows.length === 0) return;
      // Dedupe guard: if any row already has a nonzero non-lead bucket, a prior
      // full fetch or earlier merge already populated them — nothing to do.
      const hasAny = rows.some(
        (r) =>
          r.reply_turn_0 > 0 ||
          r.reply_turn_1 > 0 ||
          r.reply_turn_2 > 0 ||
          r.reply_turn_3 > 0 ||
          r.reply_turn_4 > 0 ||
          r.reply_turn_5 > 0 ||
          r.reply_turn_6 > 0 ||
          r.reply_turn_7 > 0 ||
          r.reply_turn_8 > 0 ||
          r.reply_turn_9 > 0 ||
          r.reply_turn_10 > 0 ||
          r.reply_turn_11_12 > 0 ||
          r.reply_turn_13_18 > 0 ||
          r.reply_turn_19plus > 0
      );
      if (hasAny) return;
      void (async () => {
        const rtData = await fetchReplyTurnsWithRetry(myController.signal);
        if (myController.signal.aborted) return;
        if (!rtData) return;
        // Generation guard: a newer fetchMetrics call may have started after the rt RPC
        // fired but before it resolved. Without this guard, the functional update
        // `(prev) => applyRT(prev, staleRt)` would be applied against the current
        // prev at render time, overwriting the newer range's rt buckets with stale data.
        if (metricsGenRef.current !== myGen) return;
        setMetrics((prev) => applyReplyTurnDistribution(prev, rtData));
        const cachedNow = getMetricsCache<SetterMetrics[]>(organizationId, dateRange);
        if (cachedNow)
          setMetricsCache(organizationId, dateRange, applyReplyTurnDistribution(cachedNow, rtData));
      })();
    };

    // Shared retry helper for cross-day RPC. Clones fetchReplyTurnsWithRetry shape verbatim.
    // Fires the RPC immediately; on error waits ~2500ms and fires once more. Returns data or null.
    // The first attempt already warms Postgres buffers so the retry lands fast.
    const fetchCrossDayWithRetry = async (
      signal: AbortSignal
    ): Promise<Array<Record<string, unknown>> | null> => {
      const cdParams = {
        p_organization_id: organizationId,
        p_setter_ids: setterIds,
        p_start_date: toOrgStartISO(dateRange.start!, timezone),
        p_end_date: toOrgEndISO(dateRange.end!, timezone),
      };
      const attempt = await Promise.resolve(
        supabase!.rpc("get_setter_cross_day_metrics", cdParams).abortSignal(signal)
      )
        .then((r) => ({ data: r.data, error: r.error }))
        .catch((e: unknown) => ({ data: null, error: e }));
      if (!attempt.error && attempt.data) {
        return attempt.data as Array<Record<string, unknown>>;
      }
      if (signal.aborted) return null; // aborted ≠ failed — no warn, no retry
      // First attempt failed — wait 2500ms and retry once
      console.warn("Cross-day metrics fetch failed (will retry):", attempt.error);
      await new Promise((r) => setTimeout(r, 2500));
      if (signal.aborted) return null;
      const retry = await Promise.resolve(
        supabase!.rpc("get_setter_cross_day_metrics", cdParams).abortSignal(signal)
      )
        .then((r) => ({ data: r.data, error: r.error }))
        .catch((e: unknown) => ({ data: null, error: e }));
      if (!retry.error && retry.data) {
        return retry.data as Array<Record<string, unknown>>;
      }
      console.warn("Cross-day metrics retry also failed:", retry.error);
      return null;
    };

    // Decoupled cross-day fetch for prefetch/cache early-return paths.
    // Fires only when any row has cross_day_hydrated=false (never resolved).
    // Uses fetchCrossDayWithRetry so it gets the same retry logic as the full-fetch path.
    // Fire-and-forget — never blocks render. D12 TERMINAL: null result merges [] (zeros→sections hide).
    const mergeCrossDay = (rows: SetterMetrics[]) => {
      // Skip-guard: if ALL rows are already hydrated, nothing to do (no-op on healthy paths).
      if (!rows.some((r) => !r.cross_day_hydrated)) return;
      void (async () => {
        const cdData = await fetchCrossDayWithRetry(myController.signal);
        if (myController.signal.aborted) return; // superseded by a newer fetch
        if (metricsGenRef.current !== myGen) return; // stale generation — discard
        // D12: null result → [] → applyCrossDay sets hydrated=true, zeros remain → sections hide
        const finalData = cdData ?? [];
        setMetrics((prev) => applyCrossDay(prev, finalData));
        const nowRows = getMetricsCache<SetterMetrics[]>(organizationId, dateRange);
        if (nowRows) setMetricsCache(organizationId, dateRange, applyCrossDay(nowRows, finalData));
      })();
    };

    // Stale-while-revalidate: show cached data instantly
    const cached = getMetricsCache<SetterMetrics[]>(organizationId, dateRange);
    if (cached) {
      setMetrics(cached);
      setError(null);
      // NOTE: clientSideAggregationUsed intentionally NOT set here.
      // The partial-merge at the outer-fetch setMetrics call uses `prev` (React state) to skip flicker.
      // But `prev` can hold a DIFFERENT range's data if the user rapid-clicked (e.g. This Month then Last Month).
      // Setting clientSideAggregationUsed=true here + partial merge = keeps stale prev's main metrics.
      // Cache hits use `return parsed` (full replace) — same numbers as cache, no visible flicker.
      // Recency guard: skip background re-fetch if we just fetched <60s ago
      // Prevents 2-3 redundant RPCs on every navigate-back to DM Analytics
      if (Date.now() - lastFetchTimeRef.current < RECENCY_GUARD_MS) {
        mergeOrphanLost(cached); // cache may be prefetch-written (counts hardcoded 0)
        mergeReplyTurns(cached); // cache may be prefetch-written (buckets hardcoded 0)
        mergeCrossDay(cached); // cache may be prefetch-written (cross_day_hydrated=false)
        return;
      }
      // Still fetch fresh data in background (no spinner)
    } else {
      // CLIENT-SIDE AGGREGATION: Check if we have daily rows cached for the relevant months.
      // If yes, aggregate client-side for this date range — INSTANT, no network call.
      // CRITICAL: Convert UTC dates to org-timezone dates for comparison with work_date column.
      // The RPC does `(p_start_date AT TIME ZONE v_timezone)::DATE` — we must match that.
      const startStr = getDateKeyInTZ(dateRange.start.toISOString(), timezone);
      const endStr = getDateKeyInTZ(dateRange.end.toISOString(), timezone);
      const startMonth = startStr.slice(0, 7); // "2026-03"
      const endMonth = endStr.slice(0, 7);
      const setterNames = getSetterNamesCache(organizationId);

      if (setterNames) {
        // Collect daily rows for all months in the range
        const allRows: DailyRow[] = [];
        const months = new Set([startMonth, endMonth]);
        let allMonthsCached = true;
        for (const m of months) {
          const monthRows = getDailyRowsCache<DailyRow[]>(organizationId, m);
          if (monthRows) {
            allRows.push(...monthRows);
          } else {
            allMonthsCached = false;
          }
        }

        if (allMonthsCached && allRows.length > 0) {
          // Aggregate client-side — instant!
          const clientMetrics = aggregateDailyRows(allRows, startStr, endStr, setterNames);
          if (clientMetrics.length > 0) {
            clientSideAggregationUsed = true;
            setMetrics(clientMetrics);
            setMetricsCache(organizationId, dateRange, clientMetrics);
            setError(null);
            // Don't show spinner — we have data. Background RPC will update if needed.
            // Fall through to the try/catch below for stale-while-revalidate refresh.
          }
        }
      }

      // Before showing spinner, check if prefetch has this range in-flight
      // CRITICAL: pendingKey must use the same format as useDMAnalyticsPrefetch's metricsCacheKey().
      // Both use toOrgDateKey() so the key reflects the calendar day the user selected.
      const pendingKey = `metrics_${organizationId}_${toOrgDateKey(dateRange.start)}_${toOrgDateKey(dateRange.end)}`;
      const pending = getPendingFetch<SetterMetrics[]>(pendingKey);

      if (pending) {
        // Prefetch is already fetching this range — await it instead of duplicating
        // Only show spinner if we don't already have data from client-side aggregation
        if (metrics.length === 0) setIsLoading(true);
        setError(null);
        try {
          const prefetched = await pending;
          if (myController.signal.aborted) return; // superseded — a newer range owns the UI
          if (prefetched) {
            setMetrics(prefetched);
          }
        } catch {
          // Prefetch failed — fall through to our own fetch below
        } finally {
          if (!myController.signal.aborted) setIsLoading(false); // aborted waker must not clear the new fetch's spinner
        }

        if (myController.signal.aborted) return; // covers the catch-fallthrough path

        // Check if prefetch populated the cache
        const fromCache = getMetricsCache<SetterMetrics[]>(organizationId, dateRange);
        if (fromCache && fromCache.length > 0) {
          setMetrics(fromCache);
          mergeOrphanLost(fromCache); // prefetch never fetches orphan/lost counts
          mergeReplyTurns(fromCache); // prefetch never fetches reply-turn buckets
          mergeCrossDay(fromCache); // heals abort-orphaned cache (no-op when prefetch wrote true)
          // Stamp the recency guard: prefetch data IS a full fetch (perf_fast +
          // cross_day merged before setMetricsCache). Without this stamp, every
          // range change within 60s of login re-fired the entire RPC chain even
          // though the prefetch had just warmed that exact range. Safe ONLY here —
          // the client-side aggregation path above must NOT stamp (its cross-day
          // fields are zeroed, freezing them for 60s would blank the bars).
          lastFetchTimeRef.current = Date.now();
          return; // Prefetch succeeded, no need to fetch again
        }
        // If we got here, prefetch failed or returned empty — fall through to own fetch
      }

      // Cold-cache full-RPC path: no cache, no CSA, no pending fetch covers us.
      // When CSA ran, it already populated metrics — keep them (no flicker).
      // When CSA did NOT run, the `metrics` closure is STALE — may reflect a different
      // range's data (e.g. gen=5 wrote This Month before gen=6 started Last Month).
      // We cannot rely on `metrics.length === 0` to decide. Explicitly clear + show
      // spinner so waitForSettle cannot settle on a wrong-range's data while our RPCs
      // are in-flight. React batches this setMetrics([]) with any concurrent setMetrics
      // calls; since we're the last call before the try/catch, it takes precedence.
      if (!clientSideAggregationUsed) {
        setMetrics([]);
        setIsLoading(true);
      }
    }
    setError(null);

    try {
      const rpcParams = {
        p_organization_id: organizationId,
        p_setter_ids: setterIds,
        p_start_date: toOrgStartISO(dateRange.start, timezone),
        p_end_date: toOrgEndISO(dateRange.end, timezone),
      };

      // Fire-and-forget: ensure missing dates are pre-aggregated.
      // Don't await — backfill already populated Dec 16 → Mar 12.
      // Today/yesterday will be at most 400ms stale on first load.
      supabase
        .rpc("ensure_setter_performance_fresh", rpcParams)
        .abortSignal(myController.signal)
        .then(({ error: freshErr }) => {
          if (freshErr && !myController.signal.aborted)
            console.warn("ensure_fresh background error:", freshErr);
        });

      // Reply-turn distribution is DECOUPLED from the render path. Measured
      // 2014ms warm / >8s cold-timeout as `authenticated` on Rayston
      // (/benchmark May 19, 2026): a correlated EXISTS over 41K conversations.
      // Fire it standalone via fetchReplyTurnsWithRetry so it gets one automatic
      // retry on failure, merged post-render. Mirrors the already-decoupled
      // useReplyTurnDistributionAll (window mode never hangs for exactly this reason).
      const replyTurnPromise: Promise<Array<Record<string, unknown>> | null> =
        fetchReplyTurnsWithRetry(myController.signal);

      // Cross-day metrics (~3.91s cold) are DECOUPLED from the render path.
      // Fire standalone before awaiting perf_fast so they run in parallel.
      // D1: cards paint from perf_fast (~250ms); FP+CS sections show skeletons
      // until the merge handler fires. D13: merge handler registered AFTER
      // setMetricsCache+stamp so it always sees populated state+cache.
      const crossDayPromise: Promise<Array<Record<string, unknown>> | null> =
        fetchCrossDayWithRetry(myController.signal);

      // Await ONLY the main perf_fast RPC — cross-day decoupled above.
      const mainResult = await supabase
        .rpc("get_setter_performance_fast", rpcParams)
        .abortSignal(myController.signal);
      if (myController.signal.aborted) return; // superseded — don't render a stale range

      if (mainResult.error) throw mainResult.error;

      // Parse main metrics (converts BIGINT strings to numbers).
      // cross_day_hydrated=false on every row (set by parseSetterMetrics).
      const parsed = parseSetterMetrics(mainResult.data || []);

      // NOTE: cross-day metrics are intentionally NOT merged here anymore.
      // They are decoupled above (crossDayPromise) and applied post-render
      // via applyCrossDay() in the .then() handler registered below (D13),
      // so a slow/timed-out cross-day RPC can never block the scorecard paint.

      // NOTE: reply-turn DISTRIBUTION is intentionally NOT merged here anymore.
      // It is fired standalone above (replyTurnPromise) and applied post-render.

      // Fetch orphan + lost pre-call counts in PARALLEL (independent RPCs, no shared state).
      // Separate RPCs — not in setter_daily_performance. Runs after perf_fast to
      // avoid field-name collision (orphan_count is unique; setter_ghl_user_id != setter_id
      // used by crossDay/replyTurn maps — no silent overwrite risk).
      const setterGhlIds = parsed.map((m) => m.setter_id);
      const orphanStartStr = toOrgDateKey(dateRange.start);
      const orphanEndStr = toOrgDateKey(dateRange.end);
      const chipParams2 = {
        p_organization_id: organizationId,
        p_setter_ghl_user_ids: setterGhlIds,
        p_start_date: orphanStartStr,
        p_end_date: orphanEndStr,
      };
      const [{ data: orphanData, error: orphanError }, { data: lostData, error: lostError }] =
        await Promise.all([
          supabase!
            .rpc("get_setter_orphan_pre_calls", chipParams2)
            .abortSignal(myController.signal),
          supabase!.rpc("get_setter_lost_pre_calls", chipParams2).abortSignal(myController.signal),
        ]);
      if (myController.signal.aborted) return; // superseded — don't cache a stale range
      if (orphanError) {
        console.warn("Orphan pre-calls fetch failed:", orphanError);
        for (const m of parsed) {
          m.orphan_pre_calls_error = true;
        }
      } else {
        const orphanMap: Record<string, number> = {};
        for (const row of orphanData || []) {
          orphanMap[row.setter_ghl_user_id as string] = Number(row.orphan_count ?? 0);
        }
        for (const m of parsed) {
          m.orphan_pre_calls_count = orphanMap[m.setter_id] ?? 0;
          m.orphan_pre_calls_error = false;
        }
      }
      if (lostError) {
        console.warn("Lost pre-calls fetch failed:", lostError);
        for (const m of parsed) {
          m.lost_pre_calls_error = true;
        }
      } else {
        const lostMap: Record<string, number> = {};
        for (const row of lostData || []) {
          lostMap[row.setter_ghl_user_id as string] = Number(row.lost_count ?? 0);
        }
        for (const m of parsed) {
          m.lost_pre_calls_count = lostMap[m.setter_id] ?? 0;
          m.lost_pre_calls_error = false;
        }
      }

      // D13: setMetricsCache + stamp BEFORE registering the cross-day merge handler.
      // A .then registered after Promise resolution still fires — handler always sees
      // populated state + cache. cross_day_hydrated=false on parsed rows (set by
      // parseSetterMetrics) — sections show skeletons until handler fires.
      setMetricsCache(organizationId, dateRange, parsed);
      lastFetchTimeRef.current = Date.now();

      // If client-side aggregation populated data for THIS date range, only merge non-cross-day
      // fields to avoid visible number flicker. The main metrics are the same math —
      // client-side and server-side read from the same daily table.
      // CRITICAL: Only do partial merge when clientSideAggregationUsed is true.
      // Otherwise prev may contain data from a DIFFERENT date range (stale merge bug).
      // NOTE: cross-day fields are intentionally NOT spread here (parsed has them as 0/false)
      // — the cross-day merge handler below will write them post-render via applyCrossDay.
      // The 4 orphan/lost fields MUST survive: they ARE in parsed (filled above) and not
      // cross-day fields, so they belong in this partial merge spread.
      setMetrics((prev) => {
        if (clientSideAggregationUsed && prev.length > 0 && parsed.length > 0) {
          // Merge: keep client-side main metrics, update non-cross-day fields from server.
          // cross_day_hydrated stays false on the result rows — handler fires next.
          return prev.map((existing) => {
            const serverRow = parsed.find((p) => p.setter_id === existing.setter_id);
            if (!serverRow) return existing;
            return {
              ...existing,
              // Orphan/lost counts — cross-fetch, not in daily rows
              orphan_pre_calls_count: serverRow.orphan_pre_calls_count,
              orphan_pre_calls_error: serverRow.orphan_pre_calls_error,
              lost_pre_calls_count: serverRow.lost_pre_calls_count,
              lost_pre_calls_error: serverRow.lost_pre_calls_error,
              // cross_day_hydrated stays false — cross-day handler sets it true
              cross_day_hydrated: false,
            };
          });
        }
        // No client-side data — use server data fully (cross_day_hydrated=false from parseSetterMetrics)
        return parsed;
      });

      // ── Cross-day merge: applied post-render, never blocks ─────────────────
      // The scorecard is rendered now (setMetrics above, cross_day_hydrated=false).
      // FP+CS sections show skeletons. When crossDayPromise resolves, applyCrossDay
      // sets hydrated=true on every row → sections swap to bars or hide.
      // D12 TERMINAL: cdData===null (error/exhaustion) → merge [] → zeros → hide.
      // D13: handler registered AFTER setMetricsCache+stamp above.
      void crossDayPromise.then((cdData) => {
        if (myController.signal.aborted) return; // superseded by a newer fetch
        if (metricsGenRef.current !== myGen) return; // stale generation — discard
        const finalData = cdData ?? []; // D12: null → [] → hydrated=true, zeros kept
        setMetrics((prev) => applyCrossDay(prev, finalData));
        const nowRows = getMetricsCache<SetterMetrics[]>(organizationId, dateRange);
        if (nowRows) setMetricsCache(organizationId, dateRange, applyCrossDay(nowRows, finalData));
      });

      // ── Reply-turn distribution: applied post-render, never blocks ────────
      // The scorecard is rendered now (setMetrics above). Whenever the
      // decoupled reply-turn RPC resolves (~2s warm; possibly never on a cold
      // authenticated timeout), patch its buckets into the displayed rows and
      // refresh the cache so the next load shows bars instantly. Fail-silent:
      // a reply-turn failure leaves the histogram empty but the scorecard
      // stays fully usable. Not awaited — fire-and-forget by design.
      // FIX (RANGE-RACE): re-read cache instead of using stale `parsed` — after A1,
      // `parsed` no longer carries cross-day fields, so `applyReplyTurnDistribution(parsed, rtData)`
      // would clobber the cross_day merge handler's cache write with a stale-parsed copy.
      void replyTurnPromise.then((rtData) => {
        if (myController.signal.aborted) return; // superseded by a newer fetch
        if (!rtData) return; // both attempts failed — fetchReplyTurnsWithRetry already warned
        if (metricsGenRef.current !== myGen) return; // stale generation — discard
        // state-side: functional update is already correct (prev is current)
        setMetrics((prev) => applyReplyTurnDistribution(prev, rtData));
        // cache-side: re-read NOW so cross-day merge's cache write isn't clobbered
        const nowRows = getMetricsCache<SetterMetrics[]>(organizationId, dateRange);
        if (nowRows)
          setMetricsCache(organizationId, dateRange, applyReplyTurnDistribution(nowRows, rtData));
      });
    } catch (err) {
      // .abortSignal() rejections surface as wrapped errors, not always DOMException —
      // the signal itself is the reliable source of truth.
      if (myController.signal.aborted) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (!cached) {
        console.warn("Failed to fetch setter metrics:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch metrics");
      }
    } finally {
      // An aborted (superseded) fetch must not clear the newer fetch's spinner.
      if (!myController.signal.aborted) setIsLoading(false);
    }
  }, [organizationId, setterIds, dateRange.start, dateRange.end]);

  // Combined refresh function
  const refresh = useCallback(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Initial fetch and on dependency change
  useEffect(() => {
    fetchMetrics();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchMetrics]);

  return {
    metrics,
    isLoading,
    error,
    refresh,
  };
}

// Helper to format minutes into human-readable time
export function formatMinutes(mins: number | null): string {
  if (mins === null || mins === undefined) return "-";
  if (mins < 1) return "<1 min";
  if (mins < 60) return `${Math.round(mins)} min`;
  const hours = mins / 60;
  if (hours < 24) return `${hours.toFixed(1)} hr`;
  const days = hours / 24;
  return `${days.toFixed(1)} days`;
}

// Helper to convert minute-of-day to human-readable time (e.g., 705 → "11:45 AM")
export function minuteOfDayToTime(mins: number | null): string {
  if (mins === null) return "—";
  let h = Math.floor(mins / 60);
  let m = Math.round(mins % 60);
  if (m === 60) {
    m = 0;
    h += 1;
  }
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

// Helper to get color class based on metric value
export function getMetricColor(
  value: number | null,
  thresholds: { good: number; warn: number }
): string {
  if (value === null) return "text-glass-muted";
  if (value <= thresholds.good) return "text-ds-green";
  if (value <= thresholds.warn) return "text-ds-amber";
  return "text-ds-red";
}
